import type { ActionTree, MutationTree, GetterTree } from 'vuex'
import RootState from '@web/store/types'
import type { Article, ArticleState } from '@club/types/Article'
import ArticleAPI from '@club/api/modules/article'
import type { Community } from '@club/types/Community'
import Logger from '@web/common/Logger'

const state: ArticleState = {
  articles: {}
}

const articleAPI = new ArticleAPI()

const getters: GetterTree<ArticleState, RootState> = {
  articles: ({ articles }): Article[] => Object.values(articles),
  articleById: ({ articles }) => (id: number): Article | undefined => articles[id]
}

const mutations: MutationTree<ArticleState> = {
  addArticle (state: ArticleState, article: Article) {
    state.articles[article.id] = {
      ...state.articles[article.id],
      ...article
    }
  },
  addArticles (state: ArticleState, articles: Article[]) {
    articles.forEach((article) => {
      state.articles[article.id] = article
    })
  },
  updArticleView (state: ArticleState, articleId: number) {
    // TODO inc article viewed
    Logger.info(state.articles[articleId])
  },

  logout (state: ArticleState) {
    state.articles = {}
  }
}

const actions: ActionTree<ArticleState, RootState> = {
  async fetchArticle ({ commit, rootGetters }, { articleId }: { articleId: number }) {
    if (typeof articleId !== 'number' || articleId < 0) {
      throw new Error('ArticleId is not set')
    }

    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    if (!selectedCommunity) {
      throw new Error('Need to choose a community')
    }

    const article = await articleAPI.fetch(articleId, {
      communityExternalIds: [selectedCommunity.uniq],
      width: 700,
      height: 400
    })
    const relatedArticles = await articleAPI.fetchRelatedArticles(articleId, {
      communityExternalIds: [selectedCommunity.uniq],
      width: 700,
      height: 400
    })
    commit('addArticle', {
      ...article,
      related_articles: relatedArticles?.map(item => item.id) || []
    })
    commit('addArticles', relatedArticles)
    return article
  },
  async fetchCachedArticle ({ getters, commit, dispatch, rootGetters }, { articleId }: { articleId: number }) {
    if (isNaN(articleId) || articleId < 0) {
      throw new Error('ArticleId is not set')
    }
    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    if (!selectedCommunity) {
      throw new Error('Need to choose a community')
    }
    let article = getters.articleById(articleId)
    if (article && article.description) {
      if (!article.related_articles?.length) {
        let relatedArticles: Article[] = []
        try {
          relatedArticles = await articleAPI.fetchRelatedArticles(articleId, {
            communityExternalIds: [selectedCommunity.uniq],
            width: 350,
            height: 200
          })
        } catch (e) {
          Logger.info('error - fetchRelatedArticles', e)
        }
        article = {
          ...article,
          related_articles: relatedArticles?.map(item => item.id) || []
        }
        commit('addArticle', article)
        commit('addArticles', relatedArticles)
      }

      return article
    }

    return dispatch('fetchArticle', { articleId })
  },
  async fetchRelatedArticles ({ getters, commit, rootGetters }, { articleId }: { articleId: number }) {
    if (typeof articleId !== 'number' || articleId < 0) {
      throw new Error('ArticleId is not set')
    }

    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    if (!selectedCommunity) {
      throw new Error('Need to choose a community')
    }

    const article = getters.articleById(articleId) || {}
    let relatedArticles: Article[] = []
    try {
      relatedArticles = await articleAPI.fetchRelatedArticles(articleId, {
        communityExternalIds: [selectedCommunity.uniq],
        width: 350,
        height: 200
      })
    } catch (e) {
      Logger.info('error - fetchRelatedArticles', e)
    }
    commit('addArticle', {
      ...article,
      related_articles: relatedArticles?.map(item => item.id) || []
    })
    commit('addArticles', relatedArticles)
    return relatedArticles
  },
  async setViewed ({ getters, commit, rootGetters }, { articleId }: { articleId: number }): Promise<void> {
    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    if (!selectedCommunity) {
      throw new Error('Need to choose a community')
    }

    const article = getters.articleById(articleId)
    // Не мутируем объект из state напрямую (Vuex strict) — новое значение уходит
    // в мутацию addArticle через спред.
    commit('addArticle', {
      ...article,
      views: (article.views || 0) + 1
    })

    await articleAPI.viewed(articleId, {
      communityExternalIds: [selectedCommunity.uniq]
    })
  },
  async setLike ({ getters, commit, rootGetters }, { articleId }: { articleId: number }): Promise<boolean> {
    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    if (!selectedCommunity) {
      throw new Error('Need to choose a community')
    }

    const article = getters.articleById(articleId)
    // Не мутируем объект из state напрямую (Vuex strict) — новое значение likes
    // считаем локально и передаём в мутацию addArticle через спред.
    const likes = (article.likes || 0) + (article.liked ? -1 : 1)
    commit('addArticle', {
      ...article,
      likes,
      liked: !article.liked
    })

    const isLiked = await articleAPI.like(articleId, {
      communityExternalIds: [selectedCommunity.uniq]
    })
    commit('addArticle', {
      ...article,
      likes,
      liked: isLiked.liked
    })

    // TODO(MOB-262 Task 9): событие like/favourite отправляется из composable useArticleActions (как в исходнике), с source из route

    return isLiked.liked
  },
  async addToFavourite ({ commit, getters, rootGetters }, { articleId }: { articleId: number }) {
    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    if (!selectedCommunity) {
      throw new Error('Need to choose a community')
    }

    const article = getters.articleById(articleId)
    commit('addArticle', {
      ...article,
      favorite: !article.favorite
    })

    const isFavorite = await articleAPI.favorite(articleId, {
      communityExternalIds: [selectedCommunity.uniq]
    })
    commit('addArticle', {
      ...article,
      favorite: isFavorite.favorite
    })

    // TODO(MOB-262 Task 9): событие like/favourite отправляется из composable useArticleActions (как в исходнике), с source из route

    return isFavorite.favorite
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
